import DesignServicesIcon from '@mui/icons-material/DesignServices';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BadgeIcon from '@mui/icons-material/Badge';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import HistoryIcon from '@mui/icons-material/History';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import InventoryIcon from '@mui/icons-material/Inventory';

import {
    DashboardActiveIcon,
    DashboardIcon,
    IssuesActiveIcon,
    IssuesIcon,
    LocationActiveIcon,
    LocationIcon,
    ProductActiveIcon,
    ProductIcon,
    ResellerActiveIcon,
    ResellerIcon,
    UsersActiveIcon,
    UsersIcon,
} from '@assets/index';

import type { RouteSegment } from '@/types/routes';
import { Today } from '@mui/icons-material';

export enum AppRoutes {
    Insights = '/insights',
    Locations = '/locations',
    Products = '/products',
    TodaysCuts = '/todays-cuts',
    Issues = '/issues',
    Resellers = '/resellers',
    ResellersSecondary = '/resellers/2',
    Users = '/users',
    ManageUser = '/users/user',
    Login = '/login',
    ResetPassword = '/reset',
    Models = '/models',
    Design = '/design',
    Manufacturers = '/manufacturers',
    Coverages = '/coverages',
    ManageCompany = '/manage/systems/company',
    ManageReseller = '/manage/systems/reseller',
    ManageLocation = '/manage/systems/location',
    Organizations = '/manage/organizations',
    Activity = '/activity',
    CutHistory = '/activity/history',
    ExportActivity = '/activity/export',
    BatchProcessing = '/batches',
    Invoices = '/invoices',
    BillingChanges = '/manage/billing-changes',
    StationMoveActivity = '/manage/station-move-activity',
    Employees = '/employees',
    Tickets = '/tickets',
    TicketForm = '/tickets/form',
    TroubleShooter = '/tickets/troubleshooter',
    EmployeeDetails = '/employees/employee',
    AuditTrail = '/manage/organizations/audit',
    AdminAuditLog = '/admin/audit-log',
    MaterialProductManagement = '/manage/material-products',
}

export const REPORT_ROUTES: RouteSegment = {
    name: 'Reports',
    Icon: <InsertDriveFileIcon fontSize={'small'} />,
    IconActive: <InsertDriveFileIcon fontSize={'small'} color={'primary'} />,
    routes: [
        {
            link: AppRoutes.Insights,
            label: 'Dashboard',
            Icon: <DashboardIcon />,
            IconActive: <DashboardActiveIcon />,
        },
        {
            link: AppRoutes.Resellers,
            extraActive: [AppRoutes.ResellersSecondary],
            label: 'Resellers',
            Icon: <ResellerIcon />,
            IconActive: <ResellerActiveIcon />,
        },
        {
            link: AppRoutes.Locations,
            label: 'Locations',
            Icon: <LocationIcon />,
            IconActive: <LocationActiveIcon />,
        },
        {
            link: AppRoutes.Products,
            label: 'Products',
            Icon: <ProductIcon />,
            IconActive: <ProductActiveIcon />,
        },
        {
            link: AppRoutes.Issues,
            label: 'Issues',
            Icon: <IssuesIcon />,
            IconActive: <IssuesActiveIcon />,
        },
        {
            link: AppRoutes.TodaysCuts,
            label: "Today's Cuts",
            Icon: <Today sx={{ width: '18px', height: '18px' }} />,
            IconActive: <Today sx={{ width: '18px', height: '18px' }} color={'primary'} />,
        },
        {
            link: AppRoutes.Employees,
            label: 'Employee Reports',
            Icon: <BadgeIcon sx={{ width: '18px', height: '18px' }} />,
            IconActive: <BadgeIcon sx={{ width: '18px', height: '18px' }} color={'primary'} />,
            extraActive: [AppRoutes.EmployeeDetails],
        },
    ],
};

export const ACTIVITY_ROUTES: RouteSegment = {
    name: 'Activity',
    Icon: <AssessmentIcon fontSize={'small'} />,
    IconActive: <AssessmentIcon fontSize={'small'} color={'primary'} />,
    routes: [
        {
            link: AppRoutes.Activity,
            label: 'Cut Activity',
            Icon: <AssessmentIcon fontSize={'small'} />,
            IconActive: <AssessmentIcon fontSize={'small'} color={'primary'} />,
        },
    ],
};

export const MANAGE_ROUTES: RouteSegment = {
    name: 'Manage',
    Icon: <SettingsIcon fontSize={'small'} />,
    IconActive: <SettingsIcon fontSize={'small'} color={'primary'} />,
    routes: [
        {
            link: AppRoutes.Models,
            label: 'Designs',
            Icon: <DesignServicesIcon fontSize={'small'} />,
            IconActive: <DesignServicesIcon fontSize={'small'} color={'primary'} />,
            extraActive: [AppRoutes.Coverages, AppRoutes.Design, AppRoutes.Manufacturers],
        },
        {
            link: AppRoutes.Users,
            label: 'Users',
            Icon: <UsersIcon />,
            IconActive: <UsersActiveIcon />,
        },
        {
            link: AppRoutes.Organizations,
            label: 'Organizations',
            Icon: <CorporateFareIcon />,
            IconActive: <CorporateFareIcon color={'primary'} />,
        },
        {
            link: AppRoutes.BatchProcessing,
            label: 'Batch Approval',
            Icon: <AddTaskIcon fontSize='small' />,
            IconActive: <AddTaskIcon fontSize='small' color={'primary'} />,
        },
        {
            link: AppRoutes.Invoices,
            label: 'Invoices',
            Icon: <ReceiptIcon fontSize='small' />,
            IconActive: <ReceiptIcon fontSize='small' color={'primary'} />,
        },
        {
            link: AppRoutes.BillingChanges,
            label: 'Billing Changes',
            Icon: <MonetizationOnIcon fontSize='small' />,
            IconActive: <MonetizationOnIcon fontSize='small' color={'primary'} />,
        },
        {
            link: AppRoutes.StationMoveActivity,
            label: 'Station Moves',
            Icon: <DriveFileMoveIcon fontSize='small' />,
            IconActive: <DriveFileMoveIcon fontSize='small' color={'primary'} />,
        },
        {
            link: AppRoutes.AdminAuditLog,
            label: 'Admin Audit Log',
            Icon: <HistoryIcon fontSize='small' />,
            IconActive: <HistoryIcon fontSize='small' color={'primary'} />,
        },
        {
            link: AppRoutes.MaterialProductManagement,
            label: 'Product Management',
            Icon: <InventoryIcon fontSize='small' />,
            IconActive: <InventoryIcon fontSize='small' color={'primary'} />,
        },
    ],
};

export const ROUTES = [REPORT_ROUTES, ACTIVITY_ROUTES, MANAGE_ROUTES];
